import { PaymentIntentProps } from '@nemuru-eng/checkout-js';
import {
  Agent,
  CheckoutSession,
  getLoanApplicationsByLoanId,
  getLoanById,
  LenderApprovalStatus,
  LenderName,
  Loan,
  LoanApplicationStatus,
  LoanStatus,
  LoanStatusExternal,
  Locale,
  Money,
  PaymentIntent,
  PaymentMethod,
  PaymentMethodType,
  ProcessingStrategy,
} from '@nemuru-eng/js-api-client';
import { ChooseStrategyType, postManualChooseByLoanApplicationId } from '@nemuru-eng/js-api-client/slim';

import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { PaymentMethodFactory } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/PaymentMethodFactory';
import { PaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { AbstractProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/AbstractProgress';
import { FactoryProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/FactoryProgress';
import { NIE_DATA_REQUIRED_LENDER_NAMES } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/constants/nieDataRequiredLenderNames';
import { LoanStatusExternalMap } from '@/maps/LoanStatusExternal.map';

export abstract class AbstractLoanPaymentMethodDetailEntity extends PaymentMethodDetailEntity {
  private readonly BNPL_PAYMENT_METHOD_TYPES = [PaymentMethodType.BNPL_INSTALMENTS, PaymentMethodType.BNPL_SPLIT_PAY];
  private readonly AUTO_LOAN_PAYMENT_METHOD_TYPES = [PaymentMethodType.AUTO_LOAN];

  public paymentMethod: AbstractLoanPaymentMethod | undefined = undefined;
  public hasProviderError: boolean | undefined;

  protected _loan: Loan | undefined;
  protected readonly paymentMethodOptions: PaymentMethod | undefined;
  protected _progress?: AbstractProgress;

  constructor(
    paymentMethodType: PaymentMethodType,
    checkoutSession: CheckoutSession,
    agent: Agent,
    paymentIntent?: PaymentIntent,
  ) {
    super(paymentMethodType, checkoutSession, agent, paymentIntent);
    this.paymentMethodOptions = this.getPaymentMethodOptions(checkoutSession);
  }

  public abstract refresh(): Promise<this>;
  public abstract syncWithLender(): Promise<void>;
  public abstract setPaymentMethodFromCalculator(considerAllTerms?: boolean): void;
  protected abstract setPaymentMethodFromLoan(loan: Loan): Promise<void>;

  protected getPaymentIntentMountOptions(locale?: Locale): PaymentIntentProps['options'] {
    return {
      locale: locale ?? Locale.ES_ES,
      ...(this.isBnpl()
        ? {
            behaviour: {
              disableCancelOnClose: true,
            },
          }
        : {}),
    };
  }

  public async setPaymentMethodFromLoanOrCalculator(): Promise<this> {
    if (!this.paymentMethod) {
      this.paymentMethod = PaymentMethodFactory.byType(this.type) as AbstractLoanPaymentMethod;
    }

    if (!this.paymentMethod.isSetup) {
      this.setPaymentMethodFromCalculator();
    }

    if (this._loan) {
      const providerNames = this.paymentMethod.getAllProviderNames();
      this.paymentMethod.setConditionFromLoanConditions(this._loan, providerNames);
    }

    return this;
  }

  private getPaymentMethodOptions(checkoutSession: CheckoutSession): PaymentMethod | undefined {
    return checkoutSession.paymentMethods.find((paymentMethod) => paymentMethod.type === this.type);
  }

  public reset(): this {
    this._loan = undefined;
    return super.reset();
  }

  protected updateTerm(paymentMethodEntity: AbstractLoanPaymentMethod) {
    if (this.loan && this.loan.loanConditions.loanCalculator.term.period) {
      paymentMethodEntity.updateSelectedTerm(this.loan.loanConditions.loanCalculator.term.period);
      return;
    }

    if (
      this.paymentMethodOptions &&
      'options' in this.paymentMethodOptions &&
      this.paymentMethodOptions.options &&
      'period' in this.paymentMethodOptions.options &&
      this.paymentMethodOptions.options.period
    ) {
      paymentMethodEntity.updateSelectedTerm(this.paymentMethodOptions.options.period);
      return;
    }
  }

  protected totalToFinance(): Money {
    return {
      value: this.checkoutSession.totalPrice.amount - this.checkoutSession.downPayment.amount.amount,
      currency: this.checkoutSession.totalPrice.currency,
    };
  }

  public async waitForLoanApplicationProcessing(): Promise<this> {
    const isBorrowerSelection = this.isBorrowerSelection();
    if (isBorrowerSelection) {
      await this.waitUntilAllLoanApplicationsAreProcessed();
    } else {
      await this.waitUntilSelectedApplication();
    }
    return this;
  }

  public async waitUntilSelectedApplication(): Promise<this> {
    const loan = await this.getLoanIfLoanApplicationIsSelected();
    const loanStatusExternal = LoanStatusExternalMap.fromLoanStatus(loan.status);
    this._loan = loan;
    this.status = loanStatusExternal;
    return this;
  }

  public async waitUntilAllLoanApplicationsAreProcessed(): Promise<this> {
    const loan = await this.getLoanIfAllLoanApplicationsAreProcessed();
    const loanStatusExternal = LoanStatusExternalMap.fromLoanStatus(loan.status);
    this._loan = loan;
    this.status = loanStatusExternal;
    return this;
  }

  public async chooseManualLoanApplication(loanApplicationId: string): Promise<this> {
    if (!this.paymentMethodId) {
      throw new Error('Payment method id is missing');
    }

    const isBorrowerSelection = this.isBorrowerSelection();

    if (!isBorrowerSelection) {
      throw new Error('Processing strategy is not borrower_selection for manual choose');
    }

    await postManualChooseByLoanApplicationId({ loanApplicationId, loanId: this.paymentMethodId });
    return this;
  }

  protected async getLoanIfLoanApplicationIsSelected(): Promise<Loan> {
    const paymentId = this.paymentMethodId;
    if (!paymentId) {
      throw new Error('Payment method id is missing');
    }

    let attempts = 0;
    return new Promise((resolve, reject) => {
      (async function waitForSelectedApplication() {
        attempts += 1;
        if (attempts > 30) {
          return reject();
        }

        const loan = await getLoanById({ loanId: paymentId });

        if (loan.loanApplicationSelected || loan.status === LoanStatus.STATUS_DENIED) {
          return resolve(loan);
        }

        setTimeout(waitForSelectedApplication, 2000);
      })();
    });
  }

  protected async getLoanIfAllLoanApplicationsAreProcessed(): Promise<Loan> {
    const paymentId = this.paymentMethodId;
    if (!paymentId) {
      throw new Error('Payment method id is missing');
    }

    let attempts = 0;
    return new Promise((resolve, reject) => {
      (async function waitForAllLoanApplicationsAreProcessed() {
        attempts += 1;
        if (attempts > 30) {
          return reject();
        }

        const loanApplications = await getLoanApplicationsByLoanId({ loanId: paymentId });

        if (loanApplications.length === 0) {
          return reject(new Error('No loan applications found'));
        }

        const allProcessed = loanApplications.every(
          (loanApplication) =>
            loanApplication.lenderFields.loanApplicationStatus.state === LoanApplicationStatus.PROCESSED,
        );

        if (allProcessed) {
          const loan = await getLoanById({ loanId: paymentId });
          return resolve(loan);
        }

        setTimeout(waitForAllLoanApplicationsAreProcessed, 2000);
      })();
    });
  }

  protected getLenderNamesFromProcessingStrategy(): LenderName[] {
    const processingStrategyLenderNames: LenderName[] = [];

    const processingStrategy = this.processingStrategy;

    if (processingStrategy) {
      const details = 'details' in processingStrategy ? processingStrategy.details : undefined;
      const strategyOptions = details && 'options' in details ? details.options : undefined;

      if (strategyOptions && strategyOptions.length > 0) {
        strategyOptions.forEach((option) => {
          if ('lenderName' in option) {
            processingStrategyLenderNames.push(option.lenderName as LenderName);
          }
        });
      }
    }

    return processingStrategyLenderNames;
  }

  protected getHasProviderError(): boolean {
    if (!this._loan) return false;
    return (
      this.status === LoanStatusExternal.ON_HOLD &&
      !!this._loan.loanApplicationSelected &&
      !this._loan.loanApplicationSelected.lenderFields.lenderRequestId
    );
  }

  public get loan(): Loan | undefined {
    return this._loan;
  }

  public get loanApplicationIds(): string[] | undefined {
    return this._loan?.loanApplicationIds?.map((id) => id.value) ?? undefined;
  }

  public get selectedProvider(): LenderName | undefined {
    return this._loan?.loanApplicationSelected?.lenderName;
  }

  public get lenderRequestId(): string | undefined {
    return this._loan?.loanApplicationSelected?.lenderFields.lenderRequestId ?? undefined;
  }

  public get lenderApprovalState(): LoanApplicationStatus | undefined {
    return this._loan?.loanApplicationSelected?.lenderFields.loanApplicationStatus.state ?? undefined;
  }

  public get lenderApprovalStatus(): LenderApprovalStatus | undefined {
    return this._loan?.loanApplicationSelected?.lenderFields.loanApplicationStatus.lenderApprovalStatus ?? undefined;
  }

  public get processingStrategy(): ProcessingStrategy | undefined {
    if (!this.paymentMethodOptions) return;
    if ('options' in this.paymentMethodOptions === false) return;
    if (!this.paymentMethodOptions.options) return;
    if ('processingStrategy' in this.paymentMethodOptions.options === false) return;
    return this.paymentMethodOptions.options.processingStrategy;
  }

  public isBnpl(): boolean {
    return this.BNPL_PAYMENT_METHOD_TYPES.includes(this.type);
  }

  public isAuto(): boolean {
    return this.AUTO_LOAN_PAYMENT_METHOD_TYPES.includes(this.type);
  }

  public requiresNieData(): boolean {
    if (!this.isAuto() || !this.paymentMethod) {
      return false;
    }

    const lenderNames = this.paymentMethod.getAllProviderNames();

    return lenderNames.some((lenderName) => NIE_DATA_REQUIRED_LENDER_NAMES.includes(lenderName));
  }

  public isCreditLine(): boolean {
    return this.type === PaymentMethodType.CREDIT_LINE;
  }

  public isSigned(): boolean {
    if (!this._loan) return false;
    if (!this._loan.loanApplicationSelected) return false;
    return this._loan.loanApplicationSelected.loanLenderBorrowerContract.isSigned;
  }

  public isBorrowerSelection(): boolean {
    return this.processingStrategy?.details?.orderBy === ChooseStrategyType.BORROWER_SELECTION;
  }

  protected async getProgress(): Promise<AbstractProgress> {
    if (!this.paymentMethodId) {
      throw new Error('PaymentMethodId is not set to get progress');
    }

    const progress = FactoryProgress.create(this.paymentMethodId, this.type);
    return await progress.calculateProgress();
  }

  public get progress(): AbstractProgress | undefined {
    return this._progress;
  }
}
